import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Fab from '@material-ui/core/Fab';
import Grid from '@material-ui/core/Grid';
import blue from '@material-ui/core/colors/blue';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';

// Components
import { Button, SvgList } from '..';
import SongPlayer from '../SongPlayer';
import { songActions } from '../../actions';
import './SongDetail.scss';
import useClickOutside from '../../utils/useClickOutside';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Popover,
  Typography,
} from '@material-ui/core';
import DropDownIconSvg from '../Svgs/DropdownIcon.svg';
import { GENRE } from '../../constants/songs.constant';
import { _showAlert } from '../../utils';
import Popup from '../Popup';

// Images
import pdf from '../../assets/pdf.png';
import { map, set } from 'lodash';
import axios from 'axios';

const useStyles = makeStyles({
  cancelButton: {
    borderRadius: 46,
    opacity: 0.5,
    background: '#40189D',
    boxShadow: 'none',
    padding: '12px 28px',
  },

  cardContainer: {
    width: '100px',
    margin: '10px',
  },
  input: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    zIndex: 10,
    cursor: 'pointer',
  },
  button: {
    color: blue[900],
    margin: 10,
    position: 'absolute',
    bottom: 0,
    left: 0,
    borderRadius: 6,
    width: 60,
    height: 60,
    pointerEvents: 'none',
  },
  cardactionarea: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    borderRadius: 6,
    width: 60,
    height: 60,
    overflow: 'hidden',
    zIndex: 11,
  },
  logo: {
    width: '100px',
    height: '100px',
  },
  submit: {
    width: '120px',
  },
});

function SongDetail(props) {
  const classes = useStyles();
  const history = useHistory();

  // Get Query Params from the URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');

  const toolTipRef = useRef(null);
  const [formFields, setFormFields] = useState({
    title: '',
    artist_name: '',
    description: '',
    crew_credits: '',
    thumbnail_img: '',
    license: '',
    termsandconditions: '',
    created_by: '',
  });

  const [errors, setErrors] = useState();
  const [readOnlyFields, setReadOnlyFields] = useState({
    username: true,
    password: true,
  });
  const [fetchError, setFetchError] = useState(null);
  const [uploadState, setUploadState] = useState('initial');
  const [songDetail, setSongDetail] = useState({
    multipleOwners: false,
    collectionRights: [],
  });
  const [isSongDialogOpen, setSongDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChanged, setIsChanged] = useState(false);
  const [genre, setGenre] = useState([]);
  const [plan, setPlan] = useState([]);
  const [rejectPopUp, setRejectPopup] = useState(false);
  const [reason, setReason] = useState('');
  const [isConfirm, setIsConfirm] = useState(false);
  const [approvePopup, setApprovePopup] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(1);
  const [declineAnchorEl, setDeclineAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);

  useClickOutside(toolTipRef, () => {
    setSongDialogOpen(false);
  });

  const handleUploadClick = (event) => {
    event.preventDefault();
    var file = event.target.files[0];
    
    if (!file) {
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/tiff', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ ...errors, thumbnail: 'Invalid file type. Please upload JPEG, PNG, TIFF, or WebP image.' });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setErrors({ ...errors, thumbnail: 'File size too large. Maximum size is 5MB.' });
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = function (e) {
      setFormFields({
        ...formFields,
        profilePhoto: {
          file: file,
          preview: reader.result,
        },
      });
      setUploadState('uploaded');
      // Clear any previous thumbnail errors
      if (errors?.thumbnail) {
        const { thumbnail, ...restErrors } = errors;
        setErrors(restErrors);
      }
    };
    
    reader.onerror = function () {
      setErrors({ ...errors, thumbnail: 'Failed to read file. Please try again.' });
    };
  };

  function arraysEqual(arr1, arr2) {
    // Check if both arrays have the same length
    if (arr1?.length !== arr2?.length) {
      return false;
    }

    // Sort the arrays and convert them to strings for comparison
    const sortedArr1 = arr1?.slice().sort().toString();
    const sortedArr2 = arr2?.slice().sort().toString();

    // Compare the sorted strings
    return sortedArr1 === sortedArr2;
  }

  const handleResetClick = (event) => {
    setFormFields({
      ...formFields,
      profilePhoto: '',
    });
    setUploadState('initial');
  };

  const handleInputChange = (event) => {
    let id = event.target.id;
    let value = event.target.value;
    
    // Real-time validation
    const newErrors = { ...errors };
    
    // Clear error for this field when user starts typing
    if (newErrors[id]) {
      delete newErrors[id];
      setErrors(newErrors);
    }
    
    // Validate required fields in real-time
    if (id === 'title' && value.trim() === '') {
      setErrors({ ...newErrors, title: 'Title is required' });
    } else if (id === 'description' && value.trim() === '') {
      setErrors({ ...newErrors, description: 'Description is required' });
    } else if (id === 'artist_name' && value.trim() === '') {
      setErrors({ ...newErrors, artist_name: 'Artist Name is required' });
    }
    
    setFormFields({ ...formFields, [id]: value });
  };

  const uploadAvatar = async (file) => {
    if (!file) {
      setErrors({ ...errors, thumbnail: 'No file selected for upload.' });
      return false;
    }

    // Keep same UX as before: use the main loading flag.
    setIsLoading(true);
    try {
      const res = await songActions.uploadAvatar(id, file);
      if (res) {
        setIsLoading(false);
        _showAlert('Thumbnail uploaded successfully', 'success');
        return true;
      } else {
        setIsLoading(false);
        setErrors({ ...errors, thumbnail: 'Failed to upload thumbnail. Please try again.' });
        _showAlert('Failed to upload thumbnail', 'error');
        return false;
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage = error?.message || 'Failed to upload thumbnail. Please try again.';
      setErrors({ ...errors, thumbnail: errorMessage });
      _showAlert('Failed to upload thumbnail', 'error');
      return false;
    }
  };

  const handleSaveChanges = async () => {
    // validate Empty Fields
    if (formFields.title === '') {
      setErrors({ title: 'Title is required' });
      return;
    }

    if (formFields.description === '') {
      setErrors({ description: 'Description is required' });
      return;
    }

    if (formFields.artist_name === '') {
      setErrors({ artist_name: 'Artist Name is required' });
      return;
    }

    if (plan.length === 0) {
      setErrors({ plan: 'Plan Select one plan' });
      return;
    }

    // Check which fields are different in formfields and artist
    const changedFields = Object.keys(formFields).filter((key) => formFields[key] !== songDetail[key]);
    const changedData = {};
    let uploadSuccess = true;
    
    for (const key of changedFields) {
      if (key !== 'profilePhoto') {
        changedData[key] = formFields[key];
      } else {
        // Upload avatar and wait for result
        const uploadResult = await uploadAvatar(formFields[key].file);
        if (!uploadResult) {
          uploadSuccess = false;
        }
      }
    }
    
    // If avatar upload failed, don't proceed with other updates
    if (!uploadSuccess) {
      return;
    }

    let isPlanChanged = !arraysEqual(plan, songDetail?.plan);
    let isGenreChanged = !arraysEqual(genre, songDetail?.genre);
    if (isPlanChanged) {
      changedData['plan'] = plan;
    }

    if (isGenreChanged) {
      changedData['genre'] = genre;
    }

    if (Object.keys(changedData).length !== 0) {
      setIsLoading(true);
      try {
        const res = await songActions.updateSong(id, changedData);

        if (res) {
          setIsChanged(false);
          setIsLoading(false);
          setErrors({});
          _showAlert('Song updated successfully', 'success');
          // Refresh song data to get updated values
          const updatedResponse = await songActions.fetchSongById(id);
          if (updatedResponse) {
            setSongDetail({
              ...songDetail,
              ...Object.keys(changedData).reduce((acc, key) => {
                if (key === 'title') acc.song_name = changedData[key];
                if (key === 'artist_name') acc.artist_name = changedData[key];
                if (key === 'description') acc.description = changedData[key];
                if (key === 'crew_credits') acc.crew_credits = changedData[key];
                return acc;
              }, {}),
            });
          }
        } else {
          setIsLoading(false);
          setErrors(res?.errors || { general: 'Failed to update song. Please try again.' });
          _showAlert('Failed to update song', 'error');
        }
      } catch (error) {
        setIsLoading(false);
        const errorMessage = error?.message || 'An error occurred while updating the song.';
        setErrors({ general: errorMessage });
        _showAlert('Failed to update song', 'error');
      }
    } else if (uploadSuccess) {
      // No changes to save, but upload was successful
      _showAlert('Thumbnail uploaded successfully', 'success');
    }
  };

  const handleGenreChange = (event) => {
    const {
      target: { value },
    } = event;

    const filteredValue = value.filter((item) => item !== '');

    if (filteredValue.length > 3) {
      setErrors({ ...errors, genre: 'You can select up to 3 genres' });
      // Don't close menu, just prevent selection
      return;
    } else {
      // Clear genre error if it exists
      if (errors?.genre) {
        const { genre, ...restErrors } = errors;
        setErrors(restErrors);
      }
      setGenre(filteredValue);
    }
  };

  const declineOpen = Boolean(declineAnchorEl);

  const handleRejectButtonClick = (id) => (e) => {
    e.stopPropagation();
    setRejectPopup(true);
    setSelectedItemId(id);
    setReason('');
  };

  const handleApproveButtonClick = (id) => (e) => {
    e.stopPropagation();
    setApprovePopup(true);
    setSelectedItemId(id);
  };

  const handleDeclineClick = (event) => {
    event.stopPropagation();
    setDeclineAnchorEl(event.currentTarget);
  };

  const handleDeclineClose = () => {
    setDeclineAnchorEl(null);
  };

  const handelApproveSong = async () => {
    setIsConfirm(true);
    try {
      const isSucess = await songActions.updateSong(selectedItemId, { submission_status: 'ACCEPTED' });
      if (isSucess) {
        setIsConfirm(false);
        setApprovePopup(false);
        setSongDetail({ ...songDetail, submission_status: 'ACCEPTED' });
        _showAlert('Song approved successfully', 'success');
      } else {
        setIsConfirm(false);
        _showAlert('Failed to approve song', 'error');
      }
    } catch (error) {
      setIsConfirm(false);
      const errorMessage = error?.message || 'An error occurred while approving the song.';
      _showAlert('Failed to approve song', 'error');
    }
  };

  const handleRejectSong = async () => {
    if (!reason || reason.trim() === '') {
      setErrors({ ...errors, rejectReason: 'Rejection reason is required' });
      return;
    }

    setIsConfirm(true);
    try {
      const isSucess = await songActions.updateSong(selectedItemId, { submission_status: 'REJECTED', decline_reason: reason });
      if (isSucess) {
        setRejectPopup(false);
        setIsConfirm(false);
        setSongDetail({ ...songDetail, submission_status: 'REJECTED', decline_reason: reason });
        setReason('');
        setErrors({ ...errors, rejectReason: '' });
        _showAlert('Song rejected successfully', 'success');
      } else {
        setIsConfirm(false);
        _showAlert('Failed to reject song', 'error');
      }
    } catch (error) {
      setIsConfirm(false);
      const errorMessage = error?.message || 'An error occurred while rejecting the song.';
      _showAlert('Failed to reject song', 'error');
    }
  };

  const donwloadDocument = async (doc_name) => {
    try {
      _showAlert('Your Document is downloading', 'success');
      const fileName = doc_name.split('/').pop();

      const blob = await songActions.fetchSongLicence({
        doc_name,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      _showAlert('Failed to download document', 'error');
    }
  };

  // const downloadLicence = async (e) => {
  //   e.preventDefault();
  //   const doc_name = songDetail.license;
  //   donwloadDocument(doc_name);
  // };

  const downloadOwenershioDoc = (path) => async (e) => {
    e.preventDefault();
    const doc_name = path;
    donwloadDocument(doc_name);
  };

  const downloadTermConditions = async (e) => {
    e.preventDefault();
    const doc_name = songDetail.termsandconditions;
    donwloadDocument(doc_name);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const response = await songActions.fetchSongById(id);
        if (response) {
          setSongDetail({
            song_name: response.title || '',
            artist_name: response.artist_name || '',
            genre: response.genre || [], // Fixed: use 'genre' instead of 'songgenre' for consistency
            description: response.description || '',
            crew_credits: response.crew_credits || '',
            thumbnail_img: response.thumbnail_img || '',
            license: response.license_name || '',
            termsandconditions: response.collectionRights[0]?.document || response.tc_sign || '',
            created_by: response.created_by || '',
            creator_id: response.creator_id || '',
            plan: response.plan || [],
            audio_track: response.audio_track || '',
            submission_status: response.submission_status || '',
            id: response.id || '',
            decline_reason: response.decline_reason || '',
            multipleOwners: response.multipleOwners || false,
            collectionRights: response.collectionRights || [],
          });

          setFormFields({
            title: response.title || '',
            artist_name: response.artist_name || '',
            description: response.description || '',
            crew_credits: response.crew_credits || '',
            thumbnail_img: response.thumbnail_img || '',
            license: response.license_name || '',
            termsandconditions: response.tc_sign || '',
            created_by: response.created_by,
          });

          setGenre(response.genre || []);
          setPlan(response.plan || []);
        } else {
          setFetchError('Failed to load song details. Please try again.');
          _showAlert('Failed to load song details', 'error');
        }
      } catch (error) {
        const errorMessage = error?.message || 'An error occurred while loading song details.';
        setFetchError(errorMessage);
        _showAlert('Failed to load song details', 'error');
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // check if any values from formfields is different from song
    let isChanged = Object.keys(formFields).some((key) => formFields[key] !== songDetail[key]);
    let isPlanChanged = !arraysEqual(plan, songDetail?.plan);
    
    // Fixed: use 'genre' instead of 'songgenre' for consistency
    let songGenre = [];
    if (Array.isArray(songDetail?.genre)) {
      songGenre = [...songDetail?.genre];
    } else if (songDetail?.genre) {
      songGenre = [songDetail?.genre];
    }
    let isGenreChanged = !arraysEqual(genre, songGenre);

    if (isPlanChanged || isChanged || isGenreChanged) {
      setIsChanged(true);
    } else {
      setIsChanged(false);
    }
  }, [formFields, genre, plan, songDetail]);

  // console.log(songDetail, 'songDetail');

  // console.log(formFields.created_by !== 'ADMIN', formFields.multipleOwners, 'formFields.created_by !== ADMIN');

  // Show error state if fetch failed
  if (fetchError && !isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        width: '100%',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h4 style={{ 
          marginBottom: 20, 
          fontSize: 20, 
          lineHeight: '32px',
          color: '#d32f2f'
        }}>
          Error loading song details
        </h4>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>
          {fetchError}
        </p>
        <Button onClick={() => window.location.reload()} className="button primary">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="new_customer_row" style={{ overflow: 'auto', margin: 0 }}>
        {errors?.general && (
          <div style={{ 
            padding: '12px 16px', 
            marginBottom: 20, 
            backgroundColor: '#ffebee', 
            color: '#d32f2f',
            borderRadius: 4,
            fontSize: 14
          }}>
            {errors.general}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h5>song details</h5>
          <div className="btn-group" style={{ display: 'flex', columnGap: 16 }}>
            {songDetail.created_by === 'ARTIST' &&
              (songDetail.submission_status === 'PENDING' ? (
                <div className="button-group">
                  <button
                    className="button reject"
                    style={{ '--button-color': '#DD4E47' }}
                    onClick={handleRejectButtonClick(songDetail.id)}
                  >
                    Reject
                  </button>
                  <button
                    style={{ '--button-color': '#6B3EB4' }}
                    className="button accept"
                    onClick={handleApproveButtonClick(songDetail.id)}
                  >
                    Accept
                  </button>
                </div>
              ) : songDetail.submission_status === 'ACCEPTED' ? (
                <>
                  <div className="button-group" style={{ display: 'inline-flex' }}>
                    <button
                      aria-describedby={'approve-popover'}
                      variant="contained"
                      className="button approve"
                      style={{ '--button-color': '#DD4E47', display: 'flex', alignItems: 'center', columnGap: 10 }}
                    >
                      Approved
                    </button>
                  </div>
                  <div onClick={handleRejectButtonClick(songDetail.id)}>
                    <SvgList name="switch-icon" />
                  </div>
                </>
              ) : (
                <>
                  <div className="button-group" style={{ display: 'inline-flex' }}>
                    {songDetail.submission_status === 'REJECTED' && (
                      <div className="button-group" style={{ alignItems: 'center' }}>
                        <button
                          aria-describedby={'decline-popover'}
                          variant="contained"
                          onClick={handleDeclineClick}
                          className="button decline"
                          style={{ '--button-color': '#DD4E47', display: 'flex', alignItems: 'center', columnGap: 10 }}
                        >
                          Declined
                          <SvgList name="question-icon" />
                        </button>

                        <Popover
                          id={'decline-popover'}
                          open={declineOpen}
                          anchorEl={declineAnchorEl}
                          onClose={handleDeclineClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          PaperProps={{
                            style: {
                              width: '264px',
                              padding: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              borderRadius: 8,
                              background: '#FFF',
                              boxShadow: '2px 2px 8px 2px rgba(0, 0, 0, 0.10)',
                            },
                          }}
                        >
                          <Typography>{songDetail.decline_reason}</Typography>
                        </Popover>

                        <div className="switch" onClick={handleApproveButtonClick(songDetail.id)}>
                          <SvgList name="switch-icon" />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ))}

            <Grid item ref={toolTipRef} className="tooltip-wrapper">
              <Button className="round_btn" onClick={() => setSongDialogOpen(true)}>
                Play Song
              </Button>
              {isSongDialogOpen && <SongPlayer songDetail={songDetail} onClose={() => setSongDialogOpen(false)} />}
            </Grid>
          </div>
        </div>

        <Popup open={rejectPopUp} onClose={() => !isConfirm && setRejectPopup(false)}>
          <div style={{ width: 470 }}>
            <h4 style={{ textAlign: 'center', marginBottom: 20, fontSize: 20, lineHeight: '32px', maxWidth: '60%', marginInline: 'auto' }}>
              Rejection reason
            </h4>
            <TextField
              label="Enter the reason why you are rejecting this song"
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errors?.rejectReason) {
                  const { rejectReason, ...restErrors } = errors;
                  setErrors(restErrors);
                }
              }}
              error={!!errors?.rejectReason}
              helperText={errors?.rejectReason}
              style={{ marginBottom: 20 }}
            />
            <div className="button-group" style={{ '--button-color': '#6900af' }}>
              <Button onClick={() => setRejectPopup(false)} className="button transparent">
                Cancel
              </Button>
              <Button isLoading={isConfirm} onClick={handleRejectSong} className="button primary">
                Confirm
              </Button>
            </div>
          </div>
        </Popup>

        <Popup open={approvePopup} onClose={() => !isConfirm && setApprovePopup(false)}>
          <h4 style={{ textAlign: 'center', marginBottom: 20, fontSize: 20, lineHeight: '32px', maxWidth: '60%', marginInline: 'auto' }}>
            Are you sure, you want to approve this song?
          </h4>
          <div className="button-group" style={{ '--button-color': '#6900af' }}>
            <Button onClick={() => setApprovePopup(false)} className="button transparent">
              Cancel
            </Button>
            <Button isLoading={isConfirm} onClick={handelApproveSong} className="button primary">
              Confirm
            </Button>
          </div>
        </Popup>

        <div className="field_row">
          <div className="field_col">
            <label>Song Name *</label>
            <input 
              type="text" 
              value={formFields.title} 
              onChange={handleInputChange} 
              id="title"
              maxLength={200}
              placeholder="Enter song name (max 200 characters)"
            />
            <span className="input-error-messages">{errors && errors['title']}</span>
            {formFields.title && (
              <span className="char-count">
                {formFields.title.length}/200 characters
              </span>
            )}
          </div>
          <div className="field_col">
            <label>Artist Name *</label>
            <input 
              type="text" 
              value={formFields.artist_name} 
              onChange={handleInputChange} 
              id="artist_name"
              maxLength={100}
              placeholder="Enter artist name (max 100 characters)"
            />
            <span className="input-error-messages">{errors && errors['artist_name']}</span>
            {formFields.artist_name && (
              <span className="char-count">
                {formFields.artist_name.length}/100 characters
              </span>
            )}
          </div>
          <div className="field_col">
            <label>Genre</label>
            <input type="text" />

            <FormControl className="input genre">
              <Select
                className="mui-selectBox"
                multiple
                displayEmpty
                value={genre}
                open={openMenu}
                onClose={() => setOpenMenu(false)}
                onOpen={() => setOpenMenu(true)}
                onChange={handleGenreChange}
                input={<OutlinedInput />}
                IconComponent={DropDownIconSvg}
                renderValue={(selected) => {
                  if (genre?.length === 0 || genre[0] === '') {
                    return <span className="dropdown_text">Select Genres</span>;
                  }

                  return genre?.join(', ');
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
              >
                {GENRE.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    <Checkbox color="primary" checked={genre?.indexOf(item.value) !== -1} style={{ color: '' }} />
                    <ListItemText primary={item.label} className="custom_Menu_Item" />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <span className="input-error-messages">{errors && errors['genre']}</span>
          </div>

          <div className="field_col description">
            <label>Description *</label>
            <textarea
              value={formFields.description}
              onChange={handleInputChange}
              id="description"
              onFocus={() => setReadOnlyFields({ ...readOnlyFields, phonenumber: false })}
              maxLength={1000}
              placeholder="Enter description (max 1000 characters)"
              rows={4}
              className="songdetail-textarea"
            />
            <span className="input-error-messages">{errors && errors['description']}</span>
            {formFields.description && (
              <span className="char-count">
                {formFields.description.length}/1000 characters
              </span>
            )}
          </div>

          <div className="field_col description">
            <label>Crew Credits</label>
            <textarea
              value={formFields.crew_credits}
              onChange={handleInputChange}
              id="crew_credits"
              onFocus={() => setReadOnlyFields({ ...readOnlyFields, phonenumber: false })}
              maxLength={500}
              placeholder="Enter crew credits (max 500 characters)"
              rows={3}
              className="songdetail-textarea"
            />
            <span className="input-error-messages">{errors && errors['crew_credits']}</span>
            {formFields.crew_credits && (
              <span className="char-count">
                {formFields.crew_credits.length}/500 characters
              </span>
            )}
          </div>

          <div className="field_col">
            <label className={uploadState !== 'uploaded' ? classes.cardRoot : classes.cardRootHide}>Thumbnail</label>
            <div style={{ position: 'relative' }}>
              <input
                accept="image/jpeg,image/png,image/tiff,image/webp"
                className={classes.input}
                id="contained-button-file"
                name="logo"
                type="file"
                onChange={handleUploadClick}
                style={{
                  paddingBottom: 0,
                  bottom: 10,
                  top: 'auto',
                  position: 'absolute',
                  height: 60,
                }}
              />
              <Fab
                component="span"
                style={{
                  backgroundImage:
                    formFields?.thumbnail_img !== '' && `url(${process.env.REACT_APP_MEDIA_URL}/${formFields?.thumbnail_img})`,
                  backgroundSize: 'cover',
                }}
                className={classes.button}
              ></Fab>
              <input type="text" value="" onChange={handleInputChange} />

              {uploadState === 'uploaded' && (
                <img src={formFields.profilePhoto.preview} alt="LOGO" className={classes.cardactionarea} onClick={handleResetClick} />
              )}
            </div>
            <span className="input-error-messages">{errors && errors['thumbnail']}</span>
          </div>

          {formFields?.created_by !== 'ADMIN' && (
            <>
              {/* <div className="field_col">
                <label>License</label>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 35, position: 'absolute' }}>
                  <div
                    style={{
                      display: 'flex',
                      borderRadius: 5,
                      background: '#F2F2F2',
                      height: 36,
                      width: 168,
                      padding: '0px 10px',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <img
                      alt="pdf"
                      width={21}
                      height={21}
                      src={pdf}
                      style={{ width: 20, height: 'auto', position: 'relative', margin: 0, borderRadius: 0 }}
                    />
                    <p>Oda License</p>
                  </div>
                  <a href="#" onClick={downloadLicence}>
                    <SvgList name="download-icon" />
                  </a>
                </div>

                <input type="text" value="" onChange={handleInputChange} id="name" />
                <span className="input-error-messages">{errors && errors['name']}</span>
              </div> */}

              <div className="field_col">
                <label>Terms and Conditions</label>

                <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 35, position: 'absolute' }}>
                  <div
                    style={{
                      display: 'flex',
                      borderRadius: 5,
                      background: '#F2F2F2',
                      height: 36,
                      width: 168,
                      padding: '0px 10px',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <img
                      alt="pdf"
                      width={21}
                      height={21}
                      src={pdf}
                      style={{ width: 20, height: 'auto', position: 'relative', margin: 0, borderRadius: 0 }}
                    />
                    <p>4 play T&C</p>
                  </div>
                  <a href="#" onClick={downloadTermConditions}>
                    <SvgList name="download-icon" />
                  </a>
                </div>
                <input type="text" value="" onChange={handleInputChange} id="name" />
                <span className="input-error-messages">{errors && errors['name']}</span>
              </div>
            </>
          )}

          <div className="field_col">
            <label>Added By</label>
            <input
              type="text"
              readOnly
              onClick={() => songDetail.created_by === 'ARTIST' && history.push(`/artists/${songDetail.creator_id}`)}
              value={formFields.created_by === 'ARTIST' ? formFields.artist_name : formFields.created_by}
              // onChange={handleInputChange}
              id="created_by"
            />
            <span className="input-error-messages">{errors && errors['created_by']}</span>
          </div>

          <div className="field_col plan">
            <label>Plan</label>
            <input type="text" />
            <div className="inner-wrapper">
              <div className="checkbox-wrapper">
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={plan?.includes('UNLIMITED')}
                      onChange={() =>
                        setPlan(plan.includes('UNLIMITED') ? plan.filter((item) => item !== 'UNLIMITED') : [...plan, 'UNLIMITED'])
                      }
                    />
                  }
                  label="Unlimited"
                />
              </div>
              <div className="checkbox-wrapper">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={plan?.includes('RADIO')}
                      onChange={() => setPlan(plan.includes('RADIO') ? plan.filter((item) => item !== 'RADIO') : [...plan, 'RADIO'])}
                      color="primary"
                    />
                  }
                  label="Radio"
                />
              </div>
            </div>
            <span className="input-error-messages">{errors && errors['plan']}</span>
          </div>
        </div>

        <div className="field_row_Owners">
          <div className="heading_Owners">
            <p>Right Owners:</p>
          </div>
          {formFields.created_by !== 'ADMIN' && songDetail.multipleOwners ? (
            songDetail.collectionRights && songDetail.collectionRights.length > 0 ? (
                songDetail.collectionRights.map((item, index) => (
                <div key={index} className="Owner_Single">
                <div className="info_Owner">
                  <label>Role</label>
                  <input type="text" disabled value={item.role} onChange={handleInputChange} />
                  {/* <span className="input-error-messages">{errors && errors['crew_credits']}</span> */}
                </div>
                <div className="info_Owner">
                  <label>First Name</label>
                  <input disabled type="text" value={item.firstName} onChange={handleInputChange} />
                  {/* <span className="input-error-messages">{errors && errors['crew_credits']}</span> */}
                </div>
                <div className="info_Owner">
                  <label>Last Name</label>
                  <input disabled type="text" value={item.lastName} onChange={handleInputChange} />
                  {/* <span className="input-error-messages">{errors && errors['crew_credits']}</span> */}
                </div>
                <div className="info_Owner">
                  <label>Doc</label>

                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 35, position: 'absolute' }}>
                    <div
                      style={{
                        display: 'flex',
                        borderRadius: 5,
                        background: '#F2F2F2',
                        height: 36,
                        width: 168,
                        padding: '0px 10px',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <img
                        alt="pdf"
                        width={21}
                        height={21}
                        src={pdf}
                        style={{ width: 20, height: 'auto', position: 'relative', margin: 0, borderRadius: 0 }}
                      />
                      <p>Rights Doc</p>
                    </div>
                    <a href="#" onClick={downloadOwenershioDoc(item.document)}>
                      <SvgList name="download-icon" />
                    </a>
                  </div>
                  {/* <input type="text" value="" onChange={handleInputChange} id="name" /> */}
                  {/* <span className="input-error-messages">{errors && errors['name']}</span> */}
                </div>
                </div>
              ))
            ) : (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center', 
                color: '#666',
                fontSize: '14px'
              }}>
                No right owners information available
              </div>
            )
          ) : (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#666',
              fontSize: '14px'
            }}>
              Right owners information is not applicable for this song
            </div>
          )}
        </div>

        <Grid item>
          <Button isLoading={isLoading} className="round_btn" disabled={!isChanged} onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </Grid>
      </div>
    </>
  );
}

export default SongDetail;

